// src/components/vocabulary/admin/WordManagement.tsx
import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaUpload,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaSpinner,
} from "react-icons/fa";

interface VocabularyWord {
  wordId?: number;
  germanWord: string;
  englishTranslation: string;
  bengaliTranslation: string;
  wordType: string;
  level: string;
  artikel?: string;
  pluralForm?: string;
  isRegularVerb?: boolean;
  oppositeWord?: string;
  meaningsEnglish: string[];
  meaningsBengali: string[];
  exampleGerman: string;
  exampleBengali: string;
  dayNumber: number;
  isActive: boolean;
}

const WordManagement: React.FC = () => {
  const API = process.env.REACT_APP_API_BASE_URL;
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [filteredWords, setFilteredWords] = useState<VocabularyWord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLevel, setSelectedLevel] = useState<string>("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [wordsPerPage, setWordsPerPage] = useState(20);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingWord, setEditingWord] = useState<VocabularyWord | null>(null);
  const [bulkJsonText, setBulkJsonText] = useState("");
  const [bulkUploadResult, setBulkUploadResult] = useState<any>(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkPreview, setBulkPreview] = useState<{ count: number; duplicates: number; errors: string[] } | null>(null);

  const emptyWord: VocabularyWord = {
    germanWord: "",
    englishTranslation: "",
    bengaliTranslation: "",
    wordType: "NOUN",
    level: "A1",
    artikel: "",
    pluralForm: "",
    isRegularVerb: true,
    oppositeWord: "",
    meaningsEnglish: [""],
    meaningsBengali: [""],
    exampleGerman: "",
    exampleBengali: "",
    dayNumber: 1,
    isActive: true,
  };

  const [formData, setFormData] = useState<VocabularyWord>(emptyWord);

  useEffect(() => {
    loadWords();
  }, []);

  useEffect(() => {
    filterWords();
  }, [searchTerm, selectedLevel, words]);

  const loadWords = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/vocabulary/words`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setWords(data);
        setFilteredWords(data);
      }
    } catch (error) {
      console.error("Failed to load words:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterWords = () => {
    let filtered = words;

    if (selectedLevel !== "ALL") {
      filtered = filtered.filter((word) => word.level === selectedLevel);
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (word) =>
          word.germanWord.toLowerCase().includes(searchTerm.toLowerCase()) ||
          word.englishTranslation
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    setFilteredWords(filtered);
    setCurrentPage(1);
  };

  const getPaginatedWords = () => {
    if (wordsPerPage === -1) {
      return {
        words: filteredWords,
        total: filteredWords.length,
        totalPages: 1,
        showingAll: true,
      };
    }

    const start = (currentPage - 1) * wordsPerPage;
    const end = start + wordsPerPage;
    return {
      words: filteredWords.slice(start, end),
      total: filteredWords.length,
      totalPages: Math.ceil(filteredWords.length / wordsPerPage),
      showingAll: false,
    };
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleWordsPerPageChange = (value: number) => {
    setWordsPerPage(value);
    setCurrentPage(1);
  };

  const handleCreateOrUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const url = editingWord
        ? `${API}/api/admin/vocabulary/words/${editingWord.wordId}`
        : `${API}/api/admin/vocabulary/words`;

      const method = editingWord ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        setShowModal(false);
        setEditingWord(null);
        setFormData(emptyWord);
        loadWords();
      } else {
        const error = await res.text();
        alert("Error: " + error);
      }
    } catch (error) {
      console.error("Error saving word:", error);
      alert("Error saving word. Please try again.");
    }
  };

  const handleDelete = async (wordId: number) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this word?"
    );
    if (!confirmed) return;

    try {
      const res = await fetch(`${API}/api/admin/vocabulary/words/${wordId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (res.ok) {
        loadWords();
      }
    } catch (error) {
      console.error("Error deleting word:", error);
    }
  };

  const parseBulkPreview = (text: string) => {
    if (!text.trim()) { setBulkPreview(null); return; }
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) { setBulkPreview({ count: 0, duplicates: 0, errors: ["Must be a JSON array"] }); return; }
      const existingKeys = new Set(words.map((w) => `${w.germanWord}||${w.level}`));
      const duplicates = parsed.filter((w: any) => existingKeys.has(`${w.germanWord}||${w.level}`)).length;
      const errors: string[] = [];
      const validTypes = ["NOUN","VERB","ADJECTIVE","ADVERB","PRONOUN","PREPOSITION","CONJUNCTION","INTERJECTION"];
      const validLevels = ["A1","A2","B1","B2"];
      parsed.forEach((w: any, i: number) => {
        if (!w.germanWord) errors.push(`[${i}] missing germanWord`);
        if (!w.englishTranslation) errors.push(`[${i}] missing englishTranslation`);
        if (w.wordType && !validTypes.includes(w.wordType)) errors.push(`[${i}] invalid wordType: ${w.wordType}`);
        if (w.level && !validLevels.includes(w.level)) errors.push(`[${i}] invalid level: ${w.level}`);
      });
      setBulkPreview({ count: parsed.length, duplicates, errors });
    } catch {
      setBulkPreview({ count: 0, duplicates: 0, errors: ["Invalid JSON"] });
    }
  };

  const handleBulkUpload = async () => {
    if (bulkUploading) return;

    setBulkUploading(true);
    setBulkUploadResult(null);

    try {
      const wordsArray = JSON.parse(bulkJsonText);

      if (!Array.isArray(wordsArray)) {
        alert("Invalid JSON: Must be an array of words");
        setBulkUploading(false);
        return;
      }

      const res = await fetch(`${API}/api/admin/vocabulary/words/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(wordsArray),
      });

      if (res.ok) {
        const result = await res.json();
        setBulkUploadResult(result);

        if (result.successCount > 0) {
          loadWords();
        }
      } else {
        const error = await res.text();
        alert("Upload failed: " + error);
      }
    } catch (error: any) {
      alert("Error: " + error.message);
    } finally {
      setBulkUploading(false);
    }
  };

  const openEditModal = (word: VocabularyWord) => {
    setEditingWord(word);
    setFormData(word);
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingWord(null);
    setFormData(emptyWord);
    setShowModal(true);
  };

  const handleMeaningChange = (
    type: "meaningsEnglish" | "meaningsBengali",
    index: number,
    value: string
  ) => {
    const newMeanings = [...formData[type]];
    newMeanings[index] = value;
    setFormData({ ...formData, [type]: newMeanings });
  };

  const addMeaning = (type: "meaningsEnglish" | "meaningsBengali") => {
    setFormData({ ...formData, [type]: [...formData[type], ""] });
  };

  const removeMeaning = (
    type: "meaningsEnglish" | "meaningsBengali",
    index: number
  ) => {
    const newMeanings = formData[type].filter((_, i) => i !== index);
    setFormData({ ...formData, [type]: newMeanings });
  };

  const {
    words: currentWords,
    total,
    totalPages,
    showingAll,
  } = getPaginatedWords();

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Word Management</h2>
        <div className="flex gap-2">
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#009fe3] to-[#0088cc] text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all"
          >
            <FaPlus /> Add Word
          </button>
          <button
            onClick={() => {
              setBulkJsonText("");
              setBulkUploadResult(null);
              setBulkPreview(null);
              setShowBulkModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all"
          >
            <FaUpload /> Bulk Upload
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by German or English word..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
            />
          </div>

          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
          >
            <option value="ALL">All Levels ({words.length})</option>
            <option value="A1">
              A1 - Beginner ({words.filter((w) => w.level === "A1").length})
            </option>
            <option value="A2">
              A2 - Elementary ({words.filter((w) => w.level === "A2").length})
            </option>
            <option value="B1">
              B1 - Intermediate ({words.filter((w) => w.level === "B1").length})
            </option>
            <option value="B2">
              B2 - Upper Intermediate (
              {words.filter((w) => w.level === "B2").length})
            </option>
          </select>
        </div>
      </div>

      {/* Words Table */}
      {loading ? (
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3]"></div>
          <p className="mt-4 text-gray-600">Loading words...</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
          {/* Stats Bar */}
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Showing {currentWords.length} of {total} words
                {showingAll && (
                  <span className="text-green-600 font-semibold ml-1">
                    (All)
                  </span>
                )}
              </span>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show:</span>
                <select
                  value={wordsPerPage}
                  onChange={(e) =>
                    handleWordsPerPageChange(Number(e.target.value))
                  }
                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={-1}>All</option>
                </select>
                <span className="text-sm text-gray-600">per page</span>
              </div>
            </div>

            {!showingAll && totalPages > 1 && (
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    German Word
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    English
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Level
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    Day
                  </th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentWords.map((word) => (
                  <tr key={word.wordId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold text-gray-800">
                      {word.germanWord}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {word.englishTranslation}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                        {word.wordType}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded font-semibold">
                        {word.level}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {word.dayNumber}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openEditModal(word)}
                        className="text-blue-600 hover:text-blue-800 mr-3"
                      >
                        <FaEdit size={18} />
                      </button>
                      <button
                        onClick={() => word.wordId && handleDelete(word.wordId)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FaTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {currentWords.length === 0 && (
              <div className="text-center py-10 text-gray-500">
                No words found
              </div>
            )}
          </div>

          {/* Pagination */}
          {!showingAll && totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <FaChevronLeft />
              </button>

              <div className="flex gap-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                        currentPage === pageNum
                          ? "bg-[#009fe3] text-white shadow-md"
                          : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </div>
      )}

      {/* CREATE/EDIT WORD MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b border-gray-200 px-6 py-4 sticky top-0 bg-white flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">
                {editingWord ? "Edit Word" : "Add New Word"}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingWord(null);
                  setFormData(emptyWord);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateOrUpdate} className="p-6 space-y-4">
              {/* German Word */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  German Word *
                </label>
                <input
                  type="text"
                  required
                  value={formData.germanWord}
                  onChange={(e) =>
                    setFormData({ ...formData, germanWord: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                  placeholder="e.g., Haus"
                />
              </div>

              {/* English & Bengali Translations */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    English Translation *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.englishTranslation}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        englishTranslation: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                    placeholder="e.g., house"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bengali Translation *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.bengaliTranslation}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        bengaliTranslation: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                    placeholder="e.g., বাড়ি"
                  />
                </div>
              </div>

              {/* Word Type, Level, and Day Number */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Word Type *
                  </label>
                  <select
                    required
                    value={formData.wordType}
                    onChange={(e) =>
                      setFormData({ ...formData, wordType: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                  >
                    <option value="NOUN">Noun (Substantiv)</option>
                    <option value="VERB">Verb</option>
                    <option value="ADJECTIVE">Adjective (Adjektiv)</option>
                    <option value="ADVERB">Adverb</option>
                    <option value="PRONOUN">Pronoun (Pronomen)</option>
                    <option value="PREPOSITION">
                      Preposition (Präposition)
                    </option>
                    <option value="CONJUNCTION">
                      Conjunction (Konjunktion)
                    </option>
                    <option value="INTERJECTION">
                      Interjection (Interjektion)
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Level *
                  </label>
                  <select
                    required
                    value={formData.level}
                    onChange={(e) =>
                      setFormData({ ...formData, level: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                  >
                    <option value="A1">A1 - Beginner</option>
                    <option value="A2">A2 - Elementary</option>
                    <option value="B1">B1 - Intermediate</option>
                    <option value="B2">B2 - Upper Intermediate</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Day Number *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.dayNumber}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        dayNumber: parseInt(e.target.value) || 1,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                  />
                </div>
              </div>

              {/* Artikel and Plural (for nouns) */}
              {formData.wordType === "NOUN" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Artikel
                    </label>
                    <select
                      value={formData.artikel || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, artikel: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                    >
                      <option value="">None</option>
                      <option value="der">der (masculine)</option>
                      <option value="die">die (feminine)</option>
                      <option value="das">das (neutral)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plural Form
                    </label>
                    <input
                      type="text"
                      value={formData.pluralForm || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, pluralForm: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                      placeholder="e.g., Häuser"
                    />
                  </div>
                </div>
              )}

              {/* Is Regular Verb (for verbs) */}
              {formData.wordType === "VERB" && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.isRegularVerb || false}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isRegularVerb: e.target.checked,
                      })
                    }
                    className="w-4 h-4 text-[#009fe3] rounded focus:ring-[#009fe3]"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Regular Verb
                  </label>
                </div>
              )}

              {/* Opposite Word (for adjectives) */}
              {formData.wordType === "ADJECTIVE" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opposite Word
                  </label>
                  <input
                    type="text"
                    value={formData.oppositeWord || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, oppositeWord: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                    placeholder="e.g., groß → klein"
                  />
                </div>
              )}

              {/* Meanings English */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meanings (English)
                </label>
                {formData.meaningsEnglish.map((meaning, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={meaning}
                      onChange={(e) =>
                        handleMeaningChange(
                          "meaningsEnglish",
                          index,
                          e.target.value
                        )
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                      placeholder="Meaning..."
                    />
                    {formData.meaningsEnglish.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeMeaning("meaningsEnglish", index)}
                        className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addMeaning("meaningsEnglish")}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Add Meaning
                </button>
              </div>

              {/* Meanings Bengali */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meanings (Bengali)
                </label>
                {formData.meaningsBengali.map((meaning, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={meaning}
                      onChange={(e) =>
                        handleMeaningChange(
                          "meaningsBengali",
                          index,
                          e.target.value
                        )
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                      placeholder="অর্থ..."
                    />
                    {formData.meaningsBengali.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeMeaning("meaningsBengali", index)}
                        className="px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addMeaning("meaningsBengali")}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Add Meaning
                </button>
              </div>

              {/* Example Sentences */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Example (German)
                </label>
                <textarea
                  value={formData.exampleGerman}
                  onChange={(e) =>
                    setFormData({ ...formData, exampleGerman: e.target.value })
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                  placeholder="e.g., Das Haus ist groß."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Example (Bengali)
                </label>
                <textarea
                  value={formData.exampleBengali}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      exampleBengali: e.target.value,
                    })
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3]"
                  placeholder="e.g., বাড়িটি বড়।"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingWord(null);
                    setFormData(emptyWord);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-[#009fe3] text-white rounded-lg hover:bg-[#0088cc]"
                >
                  {editingWord ? "Update Word" : "Create Word"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* BULK UPLOAD MODAL */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-800">
                Bulk Upload Words (JSON)
              </h3>
              <button
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkJsonText("");
                  setBulkUploadResult(null);
                  setBulkPreview(null);
                  setBulkUploading(false);
                }}
                className="text-gray-500 hover:text-gray-700"
                disabled={bulkUploading}
              >
                <FaTimes size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Format:</strong> Paste a JSON array of words.
                </p>
                <p className="text-xs text-gray-600 mb-2">
                  <strong>Valid wordType values:</strong> NOUN, VERB, ADJECTIVE,
                  ADVERB, PRONOUN, PREPOSITION, CONJUNCTION, INTERJECTION
                </p>
                <p className="text-xs text-gray-600 mb-3">
                  <strong>Valid level values:</strong> A1, A2, B1, B2
                </p>
                <strong className="text-sm text-gray-700">Example:</strong>
                <pre className="bg-white p-3 rounded text-xs overflow-x-auto mt-2">
                  {`[
  {
    "germanWord": "Haus",
    "englishTranslation": "house",
    "bengaliTranslation": "বাড়ি",
    "wordType": "NOUN",
    "level": "A1",
    "artikel": "das",
    "pluralForm": "Häuser",
    "meaningsEnglish": ["house", "home"],
    "meaningsBengali": ["বাড়ি", "ঘর"],
    "exampleGerman": "Das Haus ist groß.",
    "exampleBengali": "বাড়িটি বড়।",
    "dayNumber": 1,
    "isActive": true
  },
  {
    "germanWord": "gehen",
    "englishTranslation": "to go",
    "bengaliTranslation": "যাওয়া",
    "wordType": "VERB",
    "level": "A1",
    "isRegularVerb": false,
    "meaningsEnglish": ["to go", "to walk"],
    "meaningsBengali": ["যাওয়া", "হাঁটা"],
    "exampleGerman": "Ich gehe nach Hause.",
    "exampleBengali": "আমি বাড়ি যাচ্ছি।",
    "dayNumber": 1,
    "isActive": true
  },
  {
    "germanWord": "Hallo",
    "englishTranslation": "hello",
    "bengaliTranslation": "হ্যালো",
    "wordType": "INTERJECTION",
    "level": "A1",
    "meaningsEnglish": ["hello", "hi"],
    "meaningsBengali": ["হ্যালো", "হাই"],
    "exampleGerman": "Hallo! Wie geht es dir?",
    "exampleBengali": "হ্যালো! তুমি কেমন আছ?",
    "dayNumber": 1,
    "isActive": true
  }
]`}
                </pre>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paste JSON Array
                </label>
                <textarea
                  value={bulkJsonText}
                  onChange={(e) => { setBulkJsonText(e.target.value); parseBulkPreview(e.target.value); }}
                  disabled={bulkUploading}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] font-mono text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Paste your JSON array here..."
                />
              </div>

              {/* Live preview */}
              {bulkPreview && !bulkUploading && !bulkUploadResult && (
                <div className={`rounded-lg p-4 border ${bulkPreview.errors.length > 0 ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
                  <p className="font-semibold text-sm mb-1">
                    📋 Preview: <span className="text-blue-700">{bulkPreview.count} words</span>
                    {bulkPreview.duplicates > 0 && <span className="ml-2 text-orange-600">· {bulkPreview.duplicates} already exist (will skip)</span>}
                    {bulkPreview.duplicates === 0 && bulkPreview.count > 0 && <span className="ml-2 text-green-700">· No duplicates</span>}
                  </p>
                  {bulkPreview.errors.length > 0 && (
                    <ul className="text-xs text-red-600 list-disc list-inside mt-1 max-h-24 overflow-y-auto">
                      {bulkPreview.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  )}
                </div>
              )}

              {bulkUploading && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <FaSpinner
                      className="animate-spin text-blue-600"
                      size={20}
                    />
                    <span className="text-sm font-medium text-blue-700">
                      Uploading words... Please wait.
                    </span>
                  </div>
                </div>
              )}

              {bulkUploadResult && !bulkUploading && (
                <div
                  className={`p-4 rounded-lg ${
                    bulkUploadResult.successCount > 0
                      ? "bg-green-50 border border-green-200"
                      : "bg-red-50 border border-red-200"
                  }`}
                >
                  <p className="font-semibold mb-2">
                    ✅ Success: {bulkUploadResult.successCount} words
                  </p>
                  {bulkUploadResult.failureCount > 0 && (
                    <div>
                      <p className="font-semibold text-red-700 mb-2">
                        ❌ Failed: {bulkUploadResult.failureCount} words
                      </p>
                      <ul className="text-sm text-red-600 list-disc list-inside max-h-40 overflow-y-auto">
                        {bulkUploadResult.errors.map(
                          (error: string, i: number) => (
                            <li key={i}>{error}</li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBulkModal(false);
                  setBulkJsonText("");
                  setBulkUploadResult(null);
                  setBulkPreview(null);
                  setBulkUploading(false);
                }}
                disabled={bulkUploading}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Close
              </button>
              <button
                onClick={handleBulkUpload}
                disabled={!bulkJsonText.trim() || bulkUploading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {bulkUploading ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  "Upload Words"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordManagement;
